/**
 * The sidebar's destinations and who sees each one. The Sidebar renders from
 * this list and the omnibox offers the same pages, so a page is added, renamed
 * or hidden here once.
 */

/** What decides whether a page is listed. */
type NavGate = 'always' | 'firm-only' | 'invoicing' | 'accounting';

export const NAV_ITEMS = [
  { id: 'dashboard', to: '/dashboard', label: 'Dashboard', gate: 'always' },
  { id: 'clients', to: '/clients', label: 'Clients', gate: 'always' },
  { id: 'activities', to: '/activities', label: 'Activities', gate: 'firm-only' },
  { id: 'expenses', to: '/expenses', label: 'Expenses', gate: 'always' },
  { id: 'invoices', to: '/invoices', label: 'Invoices', gate: 'invoicing' },
  { id: 'emails', to: '/emails', label: 'Emails', gate: 'firm-only' },
  { id: 'calendar', to: '/calendar', label: 'Calendar', gate: 'firm-only' },
  { id: 'bank-accounts', to: '/bank-accounts', label: 'Bank Accounts', gate: 'accounting' },
  { id: 'accounting', to: '/accounting', label: 'Accounting', gate: 'accounting' },
  { id: 'settings', to: '/settings', label: 'Settings', gate: 'always' },
] as const satisfies readonly { id: string; to: string; label: string; gate: NavGate }[];

export type NavItem = (typeof NAV_ITEMS)[number];
export type NavItemId = NavItem['id'];

export type NavVisibilityInputs = {
  /** Accounting + Bank Accounts are visible to super-admins and to any account that has a ledger (the dogfood/pilot surface). */
  accountingVisible: boolean;
  /** The account is an administered entity (a deceased estate, an insolvent company, ...). */
  administered: boolean;
  accountLoading: boolean;
  /**
   * Invoicing follows the account's practice type; a deceased estate has no
   * sales-invoice link. `null` while that type has not loaded, which keeps the
   * link hidden.
   */
  invoicingVisible: boolean | null;
};

/**
 * Timekeeping, the email inbox and the calendar (which exists to draft
 * billable time from meetings) are firm-only surfaces: inside an
 * administered entity's books there is no separate team billing time, no
 * entity-owned inbox, and nothing to draft time from. Hidden while the
 * account is still loading too, so a link never flashes on and then off.
 */
export function firmOnlySurfacesVisible({
  administered,
  accountLoading,
}: Pick<NavVisibilityInputs, 'administered' | 'accountLoading'>): boolean {
  return !administered && !accountLoading;
}

export function visibleNavItems(inputs: NavVisibilityInputs): NavItem[] {
  const firmOnly = firmOnlySurfacesVisible(inputs);
  return NAV_ITEMS.filter((item) => {
    switch (item.gate) {
      case 'always':
        return true;
      case 'firm-only':
        return firmOnly;
      case 'invoicing':
        return inputs.invoicingVisible === true;
      case 'accounting':
        return inputs.accountingVisible;
    }
  });
}
